/**
 * Voice AI — Reusable Modal Component
 *
 * Usage:
 *   <voice-modal
 *     ?open=${this._showModal}
 *     title="Edit Option"
 *     width="540px"
 *     @modal-close=${() => { this._showModal = false; }}
 *   >
 *     <!-- body content (default slot) -->
 *     <div class="form-grid">...</div>
 *
 *     <!-- footer buttons (named slot) -->
 *     <div slot="footer">
 *       <button class="btn btn-secondary" @click=${this._close}>Cancel</button>
 *       <button class="btn btn-primary" @click=${this._save}>Save</button>
 *     </div>
 *   </voice-modal>
 *
 * Props:
 *   open       — Boolean, show/hide
 *   title      — String, header title
 *   width      — String, box width (default '540px')
 *   height     — String, fixed box height (e.g. '70vh')
 *
 * Events:
 *   modal-close — fired on overlay click or X button click
 *
 * Slots:
 *   (default) — body content, scrollable
 *   sticky    — non-scrolling content between header and body (e.g. search bar)
 *   footer    — action buttons, sticky at bottom
 */

import { LitElement, html, css } from 'lit';
import { designSystem } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class VoiceModal extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true },
    title: { type: String },
    width: { type: String },
    height: { type: String },
    zIndex: { type: Number, attribute: 'z-index' },
  };

  static styles = [
    designSystem,
    css`
      :host {
        display: none;
      }

      :host([open]) {
        display: contents;
      }

      .overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: var(--z-modal-backdrop, 1040);
        animation: fadeIn 0.15s ease-out;
      }

      .box {
        background: var(--glass-bg-modal, rgba(255, 253, 250, 0.97));
        border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.35));
        border-radius: var(--radius-xl, 16px);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        max-width: 90vw;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: slideUp 0.2s ease-out;
      }

      /* ─── Header (sticky) ─── */
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-5, 20px) var(--space-6, 24px);
        border-bottom: 1px solid var(--glass-divider, rgba(255, 255, 255, 0.2));
        flex-shrink: 0;
      }

      .title {
        font-family: var(--font-display);
        font-size: var(--text-lg, 16px);
        font-weight: var(--font-bold, 700);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .close-btn {
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        cursor: pointer;
        border-radius: var(--radius-md, 8px);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        font-size: 14px;
        flex-shrink: 0;
      }

      .close-btn:hover {
        background: rgba(0, 0, 0, 0.06);
        color: var(--color-text-primary);
      }

      /* ─── Sticky area (between header and body) ─── */
      .sticky {
        flex-shrink: 0;
      }
      .sticky-empty {
        display: none;
      }

      /* ─── Body (scrollable) ─── */
      .body {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
      }

      /* ─── Footer (sticky) ─── */
      .footer {
        display: flex;
        gap: var(--space-3, 12px);
        justify-content: flex-end;
        padding: var(--space-4, 16px) var(--space-6, 24px);
        border-top: 1px solid var(--glass-divider, rgba(255, 255, 255, 0.2));
        flex-shrink: 0;
      }

      .footer ::slotted(*) {
        display: flex;
        gap: var(--space-3, 12px);
        align-items: center;
        justify-content: flex-end;
        width: 100%;
      }

      .footer-empty {
        display: none;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideUp {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `,
  ];

  constructor() {
    super();
    this.open = false;
    this.title = '';
    this.width = '540px';
    this.height = '';
    this.zIndex = 0;
    this._hasSticky = false;
    this._hasFooter = false;
  }

  _close() {
    this.dispatchEvent(new CustomEvent('modal-close', { bubbles: true, composed: true }));
  }

  _onOverlayClick(e) {
    if (e.target === e.currentTarget) this._close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this._close();
  }

  connectedCallback() {
    super.connectedCallback();
    this._keyHandler = (e) => this._onKeyDown(e);
  }

  updated(changed) {
    if (changed.has('open')) {
      if (this.open) {
        document.addEventListener('keydown', this._keyHandler);
      } else {
        document.removeEventListener('keydown', this._keyHandler);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._keyHandler);
  }

  _onStickySlotChange(e) {
    const nodes = e.target.assignedNodes({ flatten: true });
    this._hasSticky = nodes.length > 0;
    this.requestUpdate();
  }

  _onFooterSlotChange(e) {
    const nodes = e.target.assignedNodes({ flatten: true });
    this._hasFooter = nodes.length > 0;
    this.requestUpdate();
  }

  render() {
    if (!this.open) return html``;

    return html`
      ${iconStyles}
      <div class="overlay" @click=${this._onOverlayClick} style="${this.zIndex ? `z-index:${this.zIndex}` : ''}">
        <div class="box" style="width:${this.width};${this.height ? `height:${this.height};` : ''}">
          <div class="header">
            <div class="title">${this.title}</div>
            <button class="close-btn" @click=${this._close} title="Close">
              <i class="fa-sharp fa-light fa-xmark"></i>
            </button>
          </div>
          <div class="sticky ${this._hasSticky ? '' : 'sticky-empty'}">
            <slot name="sticky" @slotchange=${this._onStickySlotChange}></slot>
          </div>
          <div class="body">
            <slot></slot>
          </div>
          <div class="footer ${this._hasFooter ? '' : 'footer-empty'}">
            <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('voice-modal', VoiceModal);
